import { SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import ProductCardSkeleton from 'components/product-card/skeleton';
import { CollectionSorting } from 'constants/collection';
import useMobile from 'hooks/common/use-mobile';
import { CollectionProduct } from 'models/collection/collection';
import { CollectionContext, CollectionProvider } from 'modules/collection/state';
import { CollectionPropTypes } from 'modules/collection/types/CollectionPropTypes';
import React, { FC, useMemo, useRef } from 'react';
import Slider from 'react-slick';
import { SWRConfiguration } from 'swr';
import { Gtm } from 'tracking';
import ProductSlider from './components/product-slider';
import style from './style.module.css';

const SLIDES_TO_SHOW = {
  MOBILE: 1,
  MOBILE_BLOG: 2,
  DESKTOP: 4,
  DESKTOP_LARGE: 3,
} as const;

const MAX_PRODUCTS = {
  BLOG: 5,
  HOME: 6,
} as const;

const SKELETON_SLIDES = 5;

interface PropTypeBase {
  centerMode?: boolean;
  elementNameTracking?: string[];
  filterSubscriptionEnabledItems?: boolean;
  isBlogPost?: boolean;
  isHomePage?: boolean;
  isSpecialLimitedSlider?: boolean;
  itemSize?: 'Normal' | 'Large' | undefined;
  additionalSlide?: React.ReactNode;
  shouldShowLoading?: boolean;
  sliderSize?: 'default' | 'medium' | 'large';
  collection?: SmartCollectionResponse;
  swrOptions?: SWRConfiguration;
  onSlideChange?: () => void;
  onTileClick?: (product: CollectionProduct) => void;
}

type PropTypes = CollectionPropTypes<PropTypeBase>;

/**
 * Generic slider for collection.
 * @TODO : Name is misleading as it is a generic component with a different design from CollectionSection (with displayAsSlider on)
 */
const CollectionSectionBestseller: FC<PropTypes> = ({
  centerMode: centerModeProp,
  collectionHandle,
  elementNameTracking,
  filterSubscriptionEnabledItems,
  isBlogPost = false,
  isHomePage = false,
  additionalSlide,
  shouldShowLoading = false,
  sliderSize,
  smartCollectionInput,
  collection: staticCollection,
  swrOptions,
  onSlideChange,
  onTileClick,
}) => {
  const isMobile = useMobile();
  const sliderRef = useRef<Slider>(null);
  const sliderProps = useMemo(() => {
    const variableWidth = isMobile && sliderSize === 'large';

    let slidesToShow: number = SLIDES_TO_SHOW.DESKTOP;
    if (isMobile) {
      slidesToShow = isBlogPost ? SLIDES_TO_SHOW.MOBILE_BLOG : SLIDES_TO_SHOW.MOBILE;
    } else if (sliderSize === 'large') {
      slidesToShow = SLIDES_TO_SHOW.DESKTOP_LARGE;
    }

    let centerMode = false;
    if (isMobile && isBlogPost) {
      centerMode = false;
    } else if (isMobile) {
      centerMode = true;
    }

    return {
      arrows: !isMobile,
      centerMode: centerModeProp ?? centerMode,
      infinite: false,
      slidesToShow,
      variableWidth,
    };
  }, [isMobile, isBlogPost, sliderSize, centerModeProp]);

  const hasTrackedRef = useRef(false);

  const afterChange = (): void => {
    // If onSlideChange is specified, it overrides existing behavior.
    // Needed for custom trackign on landing page slider
    if (onSlideChange) {
      onSlideChange();
      return;
    }

    if (!hasTrackedRef.current && collectionHandle) {
      hasTrackedRef.current = true;
      Gtm.track.scrollElement({
        element: ['Collection Section Bestseller', collectionHandle],
      });
    }
  };

  const sliderKey = collectionHandle ?? JSON.stringify(smartCollectionInput);

  return (
    // @ts-expect-error - CollectionProvider accepts union types, we pass whichever is defined
    <CollectionProvider
      collection={staticCollection}
      collectionHandle={collectionHandle}
      smartCollectionInput={smartCollectionInput}
      sortBy={CollectionSorting.DEFAULT}
      swrOptions={swrOptions}
    >
      <CollectionContext.Consumer>
        {(val) => {
          if (shouldShowLoading && val.isValidating) {
            return (
              <div className={style.slickContainer}>
                <Slider
                  key={sliderKey} // workaround to set to render the first slide in correct position
                  dots={false}
                  ref={sliderRef}
                  {...sliderProps}
                >
                  {Array.from({ length: SKELETON_SLIDES }, (_, index) => (
                    <div className="px-2 py-2" key={index}>
                      <ProductCardSkeleton isSlider />
                    </div>
                  ))}
                </Slider>
              </div>
            );
          }

          if (!val.collection) {
            return '';
          }

          let products = val.collection.products.filter((p) => p.availableForSale);

          const maxProducts = isBlogPost
            ? MAX_PRODUCTS.BLOG
            : isHomePage
              ? MAX_PRODUCTS.HOME
              : products.length;
          products = products.slice(0, maxProducts);

          const filteredProducts = filterSubscriptionEnabledItems
            ? products.filter((product) => product.tags.includes('SubscriptionEnabled'))
            : products;

          return (
            <ProductSlider
              collectionHandle={collectionHandle ?? ''}
              collectionTitle={val.collection.title}
              elementNameTracking={elementNameTracking}
              additionalSlide={additionalSlide}
              products={filteredProducts}
              sliderProps={sliderProps}
              sliderRef={sliderRef}
              sliderSize={sliderSize}
              afterChange={afterChange}
              onTileClick={onTileClick}
            />
          );
        }}
      </CollectionContext.Consumer>
    </CollectionProvider>
  );
};

export default CollectionSectionBestseller;
